<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useDetectDevice } from '@/composables/device'
import {
  DxChart,
  DxSeries,
  DxLegend,
  DxTooltip,
  DxCommonSeriesSettings,
  DxLabel,
} from 'devextreme-vue/chart'
import { planningApi } from '@/apis/planning.api'
import { useWebSiteStore } from '@/stores/web-site.store'

const { isMobile } = useDetectDevice()
const webSiteStore = useWebSiteStore()
const data = ref(null)

onMounted(async () => {
  const res = await planningApi.getGrossMarginSummaryAsync()
  data.value = res.data
  await nextTick()
  window.dispatchEvent(new Event('resize'))
})

const current = computed(() => data.value?.current ?? null)
const qoqChange = computed(() => data.value?.qoq_change ?? 0)
const quarters = computed(() => data.value?.quarters ?? [])

const currencySymbol = computed(() => webSiteStore.getDefaultCurrency()?.symbol ?? '')

const qoqColor = computed(() => {
  const v = qoqChange.value
  if (v > 0) return 'positive'
  if (v < 0) return 'negative'
  return 'warning'
})

const qoqLabel = computed(() => {
  const v = qoqChange.value
  if (v > 0) return `▲ +${v} pts QoQ`
  if (v < 0) return `▼ ${v} pts QoQ`
  return `— 0 pts QoQ`
})

const fmt = (val) => {
  if (val == null) return '—'
  const sym = currencySymbol.value
  const abs = Math.abs(val)
  const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(abs)
  return `${sym}${formatted}`
}

const customizeTooltip = ({ argument, value }) => ({
  text: `${argument}\n${value != null ? value + '%' : '—'}`,
})
</script>

<template>
  <div class="fit column gm-root">
    <div v-if="data === null" class="col flex flex-center">
      <q-spinner size="md" color="primary" />
    </div>
    <template v-else>
      <div class="gm-header q-pa-sm" :class="isMobile ? 'column' : 'row items-center'">
        <div>
          <div class="gm-pct text-weight-bold text-primary">
            {{ current?.margin_pct != null ? current.margin_pct + '%' : '—' }}
          </div>
          <div class="text-caption text-grey-7">Gross margin, this quarter</div>
        </div>
        <q-space v-if="!isMobile" />
        <q-chip
          :color="qoqColor"
          text-color="white"
          dense
          class="text-weight-bold gm-chip"
        >
          {{ qoqLabel }}
        </q-chip>
      </div>

      <div class="gm-table q-px-sm">
        <div
          v-for="row in [
            { label: 'Revenue', val: current?.revenue },
            { label: 'Staff Cost', val: current?.staff_cost },
            { label: 'Equipment Cost', val: current?.equipment_cost },
            { label: 'Margin', val: current?.margin, bold: true },
          ]"
          :key="row.label"
          class="row justify-between gm-row"
        >
          <span :class="row.bold ? 'text-weight-bold' : 'text-grey-8'">{{ row.label }}</span>
          <span :class="row.bold ? 'text-weight-bold' : ''">{{ fmt(row.val) }}</span>
        </div>
      </div>

      <!-- chart fills remaining space via absolute positioning so DevExtreme always gets a concrete size -->
      <div class="col gm-chart-wrap">
        <dx-chart :data-source="quarters" style="position: absolute; inset: 0;">
          <dx-common-series-settings type="spline" argument-field="label" />
          <dx-series value-field="margin_pct" name="Margin %" color="#1976D2">
            <dx-label :visible="true" :customize-text="(e) => e.value != null ? e.value + '%' : ''" />
          </dx-series>
          <dx-legend :visible="false" />
          <dx-tooltip :enabled="true" :customize-tooltip="customizeTooltip" />
        </dx-chart>
      </div>
    </template>
  </div>
</template>

<style scoped>
.gm-root {
  container-type: inline-size;
  overflow: hidden;
}
.gm-pct {
  font-size: clamp(1.2rem, 12cqw, 2.5rem);
  line-height: 1.1;
}
.gm-chip {
  font-size: clamp(0.6rem, 3cqw, 0.75rem);
}
.gm-table {
  flex-shrink: 0;
}
.gm-row {
  font-size: clamp(0.65rem, 3.5cqw, 0.8rem);
  border-top: 1px solid #e0e0e0;
  padding: 0.1rem 0;
}
.gm-chart-wrap {
  position: relative;
  min-height: 0;
}
</style>
